import { useEffect, useRef, useState } from 'react'
import type { Html5Qrcode } from 'html5-qrcode'
import { Keyboard, ScanLine } from 'lucide-react'
import { Hoja } from './ui/Hoja'
import { Boton, Entrada } from './ui/base'

const REGION = 'novaberry-escaner'

/** Lector de códigos de barra y QR con la cámara del dispositivo.
 *  Si la cámara no está disponible —navegador sin permiso, escritorio
 *  sin webcam— cae al ingreso manual en vez de dejar la pantalla muerta. */
export function EscanerCodigo({
  abierto,
  onCerrar,
  onLeer,
}: {
  abierto: boolean
  onCerrar: () => void
  onLeer: (codigo: string) => void
}) {
  const lectorRef = useRef<Html5Qrcode | null>(null)
  const [error, setError] = useState('')
  const [manual, setManual] = useState(false)
  const [codigoManual, setCodigoManual] = useState('')

  useEffect(() => {
    if (!abierto || manual) return
    let cancelado = false
    setError('')

    const iniciar = async () => {
      try {
        // La librería de lectura pesa ~300 kB; se descarga la primera vez
        // que alguien abre la cámara.
        const { Html5Qrcode } = await import('html5-qrcode')
        if (cancelado) return
        const lector = new Html5Qrcode(REGION, { verbose: false })
        lectorRef.current = lector
        await lector.start(
          { facingMode: 'environment' },
          { fps: 12, qrbox: { width: 260, height: 180 } },
          (texto) => {
            if (cancelado) return
            // Un pitido corto confirma la lectura sin mirar la pantalla.
            navigator.vibrate?.(60)
            onLeer(texto.trim())
          },
          () => {
            /* Cada cuadro sin código llama aquí; no es un error real. */
          },
        )
      } catch {
        if (!cancelado) {
          setError('No se pudo abrir la cámara. Revisa los permisos o escribe el código a mano.')
          setManual(true)
        }
      }
    }
    void iniciar()

    return () => {
      cancelado = true
      const lector = lectorRef.current
      lectorRef.current = null
      if (lector?.isScanning) void lector.stop().then(() => lector.clear()).catch(() => {})
    }
  }, [abierto, manual, onLeer])

  useEffect(() => {
    if (!abierto) {
      setManual(false)
      setCodigoManual('')
    }
  }, [abierto])

  return (
    <Hoja
      abierta={abierto}
      onCerrar={onCerrar}
      titulo="Escanear código"
      descripcion={manual ? 'Escribe el código del producto' : 'Apunta la cámara al código de barras'}
      ancho="sm"
    >
      {manual ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (codigoManual.trim()) onLeer(codigoManual.trim())
          }}
          className="space-y-4 pb-2"
        >
          <Entrada
            autoFocus
            value={codigoManual}
            onChange={(e) => setCodigoManual(e.target.value)}
            placeholder="Ej: 7801234567890"
            inputMode="numeric"
          />
          {error && <p className="text-[13px] text-rojo-500">{error}</p>}
          <div className="flex gap-3">
            <Boton
              type="button"
              variante="secundario"
              bloque
              onClick={() => setManual(false)}
              icono={<ScanLine className="size-[18px]" />}
            >
              Usar cámara
            </Boton>
            <Boton type="submit" bloque disabled={!codigoManual.trim()}>
              Buscar
            </Boton>
          </div>
        </form>
      ) : (
        <div className="pb-2">
          <div className="relative overflow-hidden rounded-2xl bg-piedra-950">
            <div id={REGION} className="[&_video]:!w-full [&_video]:!rounded-2xl" />
            {/* Marco guía: indica dónde poner el código. */}
            <div className="pointer-events-none absolute inset-0 grid place-items-center">
              <div className="h-32 w-56 rounded-2xl border-2 border-lima-400/80 shadow-[0_0_0_9999px_rgba(13,17,13,0.45)]" />
            </div>
          </div>
          <Boton
            variante="secundario"
            bloque
            className="mt-4"
            onClick={() => setManual(true)}
            icono={<Keyboard className="size-[18px]" />}
          >
            Escribir el código
          </Boton>
        </div>
      )}
    </Hoja>
  )
}
